/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */

import { useEffect, useState, useMemo } from 'react';
import dynamic from 'next/dynamic';
import router, { useRouter } from 'next/router';
import { observer } from 'mobx-react-lite';
import Link from 'next/link';
import Image from 'next/image';
import { useSession } from 'next-auth/react';
import { useTranslation } from 'next-i18next';
import algoliasearch from 'algoliasearch/lite';
import { InstantSearch } from 'react-instantsearch';
import { INavigation, TNavFontColor } from '@features/navigation/typescript';
import theme from '@styles/theme';
import styles from '@features/navigation/components/Navigation/Navigation.styles';
import BrandNavigationContent from '@features/navigation/components/BrandNavigationContent/BrandNavigationContent';
import ProductMenuDesktop from '@features/navigation/components/ProductMenuDesktop/ProductMenuDesktop';
import { ICategory } from '@features/categories';
import { TCategoriesTree, TServiceMap } from '@features/categories/typescript';
import useCategoryArray from '@hooks/useCategoryArray';
import layoutUiState from '@features/layout/mobX/LayoutUiState';
import CartNavigationButton from '@features/cart/components/CartNavigationButton/CartNavigationButton';
import { NAVIGATION_DESKTOP_ANIMATION_WAIT_TIME } from '@features/navigation/const';
import { useCartStore } from '@features/cart/mobx/context/CartStoreProvider';
import Heading from '@components/Heading/Heading';
import Icon from '@components/icons/Icon';
import Button from '@components/Button/Button';
import Login from '@components/icons/components/Login';
import LoginOutline from '@components/icons/components/LoginOutline';
import Search from '@components/icons/components/Search';
import MegaMenuDesktop from '@features/navigation/components/MegaMenuDesktop/MegaMenuDesktop';
import { getRegion } from '@utils/misc';
import { BASE_PATH } from '@config/index';
import getImageQuality from '@utils/getImageQuality';
import IsCustomerMiddleware from '@utils/IsCustomerMiddleware';
import { useResponsive } from '@features/responsive';
import { CLIENT_ID, CLIENT_KEY } from '@components/Search/const';
import { SearchBox } from '@components/Search/components/SearchBox/SearchBox';

const DynamicSearch = dynamic(() => import('@components/Search/Search'), {
  ssr: false,
});

type PropsType = {
  navElements: INavigation;
  categoriesTree: TCategoriesTree;
  serviceMap: TServiceMap;
  isTransparent: boolean;
  backgroundColor?: string;
  fontColorVariant?: TNavFontColor;
  withBorder?: boolean;
};

const NavigationDesktop = ({
  navElements,
  categoriesTree,
  serviceMap,
  isTransparent,
  backgroundColor = '',
  fontColorVariant = 'auto',
  withBorder = true,
}: PropsType) => {
  const { brand, stores, architects, contact, voxbox, promotions } = navElements;
  const { t } = useTranslation('common');
  const productGroup = useCategoryArray(categoriesTree);
  const { totalQuantity } = useCartStore();
  const {
    isSearchOpened,
    openSearch,
    isMenuDesktopHighlighted,
    setMenuDesktopHighlighted,
    navigationProductItemSelected,
    setNavigationProductItemSelected,
    isMenuPined,
    setMenuPined,
    closeSearch,
    userData,
    setUserData,
    setMenuMobileOpened,
    setUserPanelOpened,
  } = layoutUiState;
  const { data: session } = useSession();
  const region = getRegion();
  const { isWiderThanDesktop } = useResponsive();
  const client = useMemo(() => algoliasearch(CLIENT_ID, CLIENT_KEY), [CLIENT_ID, CLIENT_KEY]);
  const [query, setQuery] = useState('');

  const { pathname } = useRouter();

  const [forceMenuPined, setForceMenuPined] = useState(false);

  const handleOpenDesktopMenu = (product: ICategory | null) => {
    setUserData(false);
    setNavigationProductItemSelected(product);
    setForceMenuPined(true);
  };

  const handleCloseDesktopMenu = () => {
    setMenuMobileOpened(false);
    setUserData(false);
    setForceMenuPined(false);
    setNavigationProductItemSelected(null);
    setTimeout(() => {
      setMenuPined(false);
    }, NAVIGATION_DESKTOP_ANIMATION_WAIT_TIME);
  };

  const menuIsTransparent = userData ? false : isTransparent;
  const isFontDarkColorVariant = fontColorVariant === 'dark';

  const defaultColor =
    theme.rawColors[
      `${isMenuPined || isMenuDesktopHighlighted || !menuIsTransparent ? 'darkGrey700' : 'neutralWhite'}`
    ];

  const color = isFontDarkColorVariant ? 'darkGrey700' : defaultColor;

  const inputDefaultBorderColor =
    theme.rawColors[
      `${isMenuPined || isMenuDesktopHighlighted || !menuIsTransparent ? 'lightGrey700' : 'neutralWhite'}`
    ];
  const inputBorderColor = isFontDarkColorVariant ? 'lightGrey700' : inputDefaultBorderColor;

  const buttonVariant = isMenuPined || isMenuDesktopHighlighted || !menuIsTransparent ? 'dark' : 'white';
  const buttonVariantColor = fontColorVariant === 'auto' ? buttonVariant : fontColorVariant;

  const handleOpenUserPanel = () => {
    if (userData) {
      handleCloseDesktopMenu();
    } else {
      setNavigationProductItemSelected(null);
      setMenuPined(true);
      setUserData(session || true);
    }
  };

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    if (urlSearchParams.get('panel') === 'true' && session) {
      handleOpenUserPanel();
      delete router.query.panel;
      router.replace(
        {
          pathname: router.pathname,
          query: router.query,
        },
        undefined,
        { shallow: true },
      );
    }

    router.beforePopState(({ as }) => {
      if (as !== router.asPath) {
        closeSearch();
        setNavigationProductItemSelected(null);
        setMenuDesktopHighlighted(false);
        setMenuPined(false);
      }
      return true;
    });
    return () => {
      router.beforePopState(() => true);
    };
  }, [pathname, session]);

  const rightPartNavContent = (
    <div sx={styles.rightMenuContainer}>
      <IsCustomerMiddleware>
        <div sx={styles.userLoggedContainer}>
          <div sx={styles.loginIcon} onClick={handleOpenUserPanel}>
            {session ? (
              <Button
                variant="link"
                variantColor="noStyle"
                size="small"
                addSx={styles.loginButton}
                addClassName="nav_user_panel"
              >
                <Heading h={5} headingType={7} uppercase color={color}>
                  {t('welcome')}
                </Heading>
                <Icon color={color} size="24px" hover="neutralBlack" cy="WelcomeIcon">
                  <Login />
                </Icon>
              </Button>
            ) : (
              <Button
                variant="iconOnly"
                variantColor="noStyle"
                addSx={styles.loginButton}
                cy="LoginButton"
                addClassName="nav_user_panel"
              >
                <Icon color={color} size="24px">
                  <LoginOutline />
                </Icon>
              </Button>
            )}
          </div>
          <div>
            <CartNavigationButton
              iconColor={color}
              active={pathname !== '/cart' && totalQuantity > 0}
              activeOnCartPage={pathname === '/cart' && !!userData}
              onCloseMenu={handleCloseDesktopMenu}
              isDark={
                (isMenuPined || isMenuDesktopHighlighted || isFontDarkColorVariant || !menuIsTransparent) &&
                buttonVariantColor === 'dark'
              }
            />
          </div>
        </div>
      </IsCustomerMiddleware>
      <div sx={styles.inspirationContainer}>
        {/* NOTE: temporary for DEMO */}
        {!!promotions && (
          <Link href={promotions.link} prefetch={false} onClick={handleCloseDesktopMenu} className="nav_right">
            <div sx={styles.inspirationMenuItem(color)}>{promotions.value}</div>
          </Link>
        )}
        {region === 'PL' && (
          <Link href="/inspirations/ideas" prefetch={false} onClick={handleCloseDesktopMenu} className="nav_right">
            <div sx={styles.inspirationMenuItem(color)}>{t('ideas.plural')}</div>
          </Link>
        )}
        {!!voxbox && (
          <Link href={voxbox.link} prefetch={false} onClick={handleCloseDesktopMenu} className="nav_right">
            <div sx={styles.inspirationMenuItem(color)}>{voxbox.value}</div>
          </Link>
        )}
      </div>
    </div>
  );

  const handleMenuDesktopLeave = () => {
    setMenuDesktopHighlighted(false);
    if (!forceMenuPined) {
      setMenuPined(false);
    }
  };

  const searchPlaceHolder = isWiderThanDesktop ? t('search_placeholder') : t('what_are_you_looking_for');

  return (
    <>
      <nav
        sx={styles.navigationContainerDesktop(
          isMenuPined,
          menuIsTransparent,
          backgroundColor,
          withBorder,
          color === theme.rawColors.neutralWhite,
        )}
        data-cy="NavigationDesktop"
      >
        <div
          sx={styles.navigationContentContainer}
          onMouseEnter={() => setMenuDesktopHighlighted(true)}
          onMouseLeave={() => handleMenuDesktopLeave()}
        >
          <div sx={styles.menuLogoDesktop}>
            <Link href="/" onClick={handleCloseDesktopMenu}>
              <Image
                data-cy="logoIcon"
                height={52}
                width={70}
                src={`${BASE_PATH}/img/logo.svg`}
                quality={getImageQuality()}
                alt={t('logoIconAlt')}
                priority
              />
            </Link>
          </div>
          <div sx={styles.mainNavContentContainer}>
            <BrandNavigationContent
              brand={brand}
              stores={stores}
              architects={architects}
              contact={contact}
              color={color}
              isMobile={false}
              onItemClick={handleCloseDesktopMenu}
            />
            <ProductMenuDesktop
              products={productGroup}
              selectedGroup={navigationProductItemSelected}
              color={color}
              onClick={handleOpenDesktopMenu}
            />
          </div>
          <div sx={styles.menuSearchInputContainer}>
            <input
              sx={styles.menuSearchInput(color, inputBorderColor, true)}
              placeholder={t('search')}
              onClick={openSearch}
              onFocus={(e) => e.preventDefault()}
              readOnly
              data-cy="SearchInput"
              className="nav_searchbar"
            />
            <Icon size="20px" color={color} addSx={styles.searchIcon}>
              <Search />
            </Icon>
          </div>
          {rightPartNavContent}
        </div>
      </nav>
      <MegaMenuDesktop
        serviceMap={serviceMap}
        onClose={handleCloseDesktopMenu}
        userData={userData}
        products={productGroup}
        setUserPanelOpened={setUserPanelOpened}
      />

      <div sx={styles.mainContainer(isSearchOpened)} data-cy="SearchModal">
        <InstantSearch searchClient={client} future={{ preserveSharedStateOnUnmount: false }} insights>
          <SearchBox placeholder={searchPlaceHolder} setQuery={setQuery} isSearchOpened={isSearchOpened} />
          {isSearchOpened && (
            // @ts-expect-error - React 19 compatibility issue with next/dynamic types
            <DynamicSearch serviceMap={serviceMap} query={query} />
          )}
        </InstantSearch>
      </div>
    </>
  );
};

export default observer(NavigationDesktop);
